import { Header } from "semantic-ui-react";
import { useHistory, useParams } from "react-router-dom";
import { useEffect, useState } from "react";

import {
  InputNumber,
  InputSelect,
  InputText,
  InputToggle,
} from "../../../components/Inputs/Inputs";
import BackButton from "../../../components/BackButton/BackButton";
import { api } from "../../../api/api";
import { useToast } from "../../../components/Toast";
import {
  country_url,
  order_status_url,
  recon_charges_url,
  sac_tax_code_url,
} from "../../../api/urls";
import unknownError from "../../../utils/unknownError";
import { moveUpToError } from "../../../utils/moveUpToError";
import Loading from "../../../components/Loading/Loading";
import { modules } from "../../../api/codes";
import { ApprovalText } from "../../../utils/approveLevel";
import { APIDropdown } from "../../../components/APIDropdown/APIDropdown";
import { generateUID } from "../../../utils/generateUID";

const initialState = {
  printable_name: "",
  disable: false,
  is_active: false,
  charge_name: "",
  status: "",
};

const module = modules.SellerCharges;

const title = "Seller Charges";
const backUrl = "/masters/seller_charges";
const apiUrl = recon_charges_url;

const NewSellerCharges = ({ edit, view }) => {
  const [data, setData] = useState(initialState);
  const [errors, setErrors] = useState({});
  const history = useHistory();
  const params = useParams();
  const toast = useToast();
  const [isGetting, setIsGetting] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [aprvlLoader, setAprvlLoader] = useState(false);
  const [refresh, setRefresh] = useState(false);

  useEffect(() => {
    if (edit || view || params.id) {
      const getData = async () => {
        try {
          const res = await api.get(apiUrl + params.id + "/", {
            headers: {
              module: module,
            },
          });
          setData(res.data);
          setIsGetting(false);
        } catch (err) {
          unknownError(err);
          setIsGetting(false);
        }
      };
      getData();
    } else {
      setIsGetting(false);
    }
  }, [params.id, edit, view, refresh]);

  const set = (key, value) => {
    setData((d) => {
      const newData = { ...d };
      newData[key] = value;
      return newData;
    });
  };

  if (isGetting) return <Loading />;

  return (
    <form
      id="form-wrapper"
      onSubmit={(e) => {
        e.preventDefault();
        setIsSaving(true);
        try {
          const payload = {
            ...data,
            code: generateUID(),
            printable_name: data?.charge_name,
          };
          if (edit && params.id) {
            api
              .patch(apiUrl + data.id + "/", payload, {
                headers: {
                  "Content-Type": "application/json",
                  module: module,
                },
              })
              .then((res) => {
                toast.open("Edited data with id: " + res.data.id, "success");
                history.push(backUrl);
                setIsSaving(false);
              })
              .catch((err) => {
                if (err.response.data) {
                  setErrors(err.response.data);
                  moveUpToError();
                } else {
                  unknownError(err);
                }
                setIsSaving(false);
              });
          } else {
            api
              .post(apiUrl, data, {
                headers: {
                  "Content-Type": "application/json",
                  module: module,
                },
              })
              .then((res) => {
                toast.open("Added data with id: " + res.data.id, "success");
                history.push(backUrl);
                setIsSaving(false);
              })
              .catch((err) => {
                if (err.response.data) {
                  setErrors(err.response.data);
                  moveUpToError();
                } else {
                  unknownError(err);
                }
                setIsSaving(false);
              });
          }
        } catch (err) {
          unknownError(err);
          setIsSaving(false);
        }
      }}
    >
      <div id="form">
        <BackButton href={backUrl} />
        <Header>{title}</Header>

        <InputText
          label="Charge name"
          placeholder="Charge name"
          disabled={view}
          value={data.charge_name}
          error={errors["charge_name"]}
          required
          onChange={(v) => set("charge_name", v)}
        />
        <APIDropdown
          label="Country"
          placeholder="Country"
          required
          disabled={view}
          value={data.country_tax}
          url={country_url}
          onChange={(v) => set("country_tax", v)}
          error={errors["country_tax"]}
          module={module}
        />

        <APIDropdown
          label="SAC code"
          placeholder="SAC code"
          required
          disabled={view}
          value={data.tax_code}
          url={sac_tax_code_url}
          onChange={(v) => set("tax_code", v)}
          error={errors["tax_code"]}
          module={module}
        />
        <InputSelect
          label="Charge Type"
          placeholder="Charge Type"
          disabled={view}
          value={data.percent_or_fix}
          error={errors["percent_or_fix"]}
          required
          onChange={(v) => set("percent_or_fix", v)}
          options={["PERCENT", "FIX", "BOTH"]}
        />
        {(data.percent_or_fix === "FIX" || data.percent_or_fix === "BOTH") && (
          <InputNumber
            label="Minimum Charge"
            placeholder="Minimum Charge"
            disabled={view}
            required={
              data.percent_or_fix === "FIX" || data.percent_or_fix === "BOTH"
            }
            value={data.price_value}
            onChange={(v) => set("price_value", v)}
          />
        )}
        {(data.percent_or_fix === "PERCENT" ||
          data.percent_or_fix === "BOTH") && (
          <InputNumber
            label="Charge Percentage (Of total product MRP)"
            placeholder="Charge Percentage (Of total product MRP)"
            disabled={view}
            required={
              data.percent_or_fix === "PERCENT" ||
              data.percent_or_fix === "BOTH"
            }
            value={data.charge_percent}
            onChange={(v) => set("charge_percent", v)}
          />
        )}

        <APIDropdown
          label="Order Status"
          placeholder="Order Status"
          required
          disabled={view}
          value={data.status}
          url={order_status_url}
          onChange={(v) => set("status", v)}
          error={errors["status"]}
          module={module}
        />
        <InputToggle
          label="Active"
          disabled={view}
          value={data.is_active}
          error={errors["is_active"]}
          onChange={(v) => set("is_active", v)}
        />

        <ApprovalText
          id={params.id}
          level={data?.approval}
          setRefresh={setRefresh}
          setAprvlLoader={setAprvlLoader}
          toast={toast}
          view={view}
          aprvlLoader={aprvlLoader}
          module={module}
          apiUrl={apiUrl}
        />

        {!view && (
          <>
            <div style={{ padding: "10px" }} />
            <div className="actions">
              <button
                className="btn-red"
                type="button"
                onClick={() => {
                  history.push(backUrl);
                }}
              >
                Cancel
              </button>
              <button
                className="btn"
                onClick={() => {
                  moveUpToError();
                }}
                disabled={isSaving}
              >
                Save
              </button>
            </div>
          </>
        )}
        <div style={{ padding: "10px" }} />
      </div>
    </form>
  );
};

export default NewSellerCharges;
